<template>
  <div>
    <h2>Total Assignments</h2>
  </div>
  <div class="chart-container">
    <!-- Age Range Donut Chart -->
    <div class="chart-card">
      <h3>By Age Range</h3>
      <div class="chart-content">
        <apexchart
          v-if="hasChartData(chartData.ageRangeSeries)"
          type="donut"
          :options="{ ...chartData.ageRangeOptions }"
          :series="chartData.ageRangeSeries"
          height="300"
        ></apexchart>
        <div v-else class="chart-empty-state">No data available</div>
      </div>
    </div>

    <!-- Topic Donut Chart -->
    <div class="chart-card">
      <h3>By Topic</h3>
      <div class="chart-content">
        <apexchart
          v-if="hasChartData(chartData.topicSeries)"
          type="donut"
          :options="chartData.topicOptions"
          :series="chartData.topicSeries"
          height="300"
        ></apexchart>
        <div v-else class="chart-empty-state">No data available</div>
      </div>
    </div>

    <!-- Subject Area Donut Chart -->
    <div class="chart-card">
      <h3>By Subject Area</h3>
      <div class="chart-content">
        <apexchart
          v-if="hasChartData(chartData.subjectAreaSeries)"
          type="donut"
          :options="chartData.subjectAreaOptions"
          :series="chartData.subjectAreaSeries"
          height="300"
        ></apexchart>
        <div v-else class="chart-empty-state">No data available</div>
      </div>
    </div>

    <!-- Language Donut Chart -->
    <div class="chart-card">
      <h3>By Language</h3>
      <div class="chart-content">
        <apexchart
          v-if="hasChartData(chartData.languageSeries)"
          type="donut"
          :options="chartData.languageOptions"
          :series="chartData.languageSeries"
          height="300"
        ></apexchart>
        <div v-else class="chart-empty-state">No data available</div>
      </div>
    </div>
  </div>
</template>

<script setup>
import { ref } from "vue";
import { sfiStore } from "@/services/sfiService";

const ageRange = sfiStore.data.chartData.ageRange;
const topicCount = sfiStore.data.chartData.topic;
const subjectCount = sfiStore.data.chartData.subject;
const languageCount = sfiStore.data.chartData.language;

const hasChartData = (series) => {
  return Array.isArray(series) && series.some((value) => Number(value) > 0);
};

const chartData = ref({
  // Age Range Chart Data
  ageRangeSeries:
    ageRange && ageRange.length ? ageRange.map((item) => item.count) : [],
  ageRangeOptions: {
    chart: {
      type: "donut",
    },
    labels:
      ageRange && ageRange.length
        ? ageRange.map((item) => "Age " + item.name)
        : [],
    colors: ["#004D79", "#00AC69", "#E4006D"],
    legend: {
      position: "right",
    },
    plotOptions: {
      pie: {
        donut: {
          size: "45%",
          labels: {
            show: true,
            name: {
              show: false,
            },
            value: {
              show: true,
              fontSize: "var(--fs-large)",
              fontWeight: "bold",
              formatter: function (val) {
                return val + " sp";
              },
            },
            total: {
              show: false,
            },
          },
        },
        dataLabels: {
          offset: 0,
          minAngleToShowLabel: 10,
        },
      },
    },
    dataLabels: {
      enabled: true,
      formatter: function (val) {
        return val.toFixed(1) + "%";
      },
      style: {
        fontSize: "var(--fs-small)",
        fontFamily: "Arial, sans-serif",
        fontWeight: "bold",
        colors: ["#fff"],
      },
      dropShadow: {
        enabled: false,
      },
    },
    responsive: [
      {
        breakpoint: 480,
        options: {
          legend: {
            position: "bottom",
          },
        },
      },
    ],
  },

  // Topic Chart Data
  topicSeries:
    topicCount && topicCount.length ? topicCount.map((item) => item.count) : [],
  topicOptions: {
    chart: {
      type: "donut",
    },
    labels:
      topicCount && topicCount.length
        ? topicCount.map((item) => item.name)
        : [],
    colors: ["#4C4DDC", "#026843", "#8C0142", "#E00069", "#00355C", "#C21D1D"],
    legend: {
      position: "right",
    },
    plotOptions: {
      pie: {
        donut: {
          size: "45%",
          labels: {
            show: true,
            name: {
              show: false,
            },
            value: {
              show: true,
              fontSize: "var(--fs-large)",
              fontWeight: "bold",
              formatter: function (val) {
                return val + " sp";
              },
            },
            total: {
              show: false,
            },
          },
        },
        dataLabels: {
          offset: 0,
          minAngleToShowLabel: 10,
        },
      },
    },
    dataLabels: {
      enabled: true,
      formatter: function (val) {
        return val.toFixed(1) + "%";
      },
      style: {
        fontSize: "var(--fs-small)",
        fontFamily: "Arial, sans-serif",
        fontWeight: "bold",
        colors: ["#fff"],
      },
      dropShadow: {
        enabled: false,
      },
    },
    responsive: [
      {
        breakpoint: 480,
        options: {
          legend: {
            position: "bottom",
          },
        },
      },
    ],
  },

  // Subject Area Chart Data
  subjectAreaSeries:
    subjectCount && subjectCount.length
      ? subjectCount.map((item) => item.count)
      : [],
  subjectAreaOptions: {
    chart: {
      type: "donut",
    },
    labels:
      subjectCount && subjectCount.length
        ? subjectCount.map((item) => item.name)
        : [],
    colors: ["#004D79", "#00AC69", "#E4006D"],
    legend: {
      position: "right",
    },
    plotOptions: {
      pie: {
        donut: {
          size: "45%",
          labels: {
            show: true,
            name: {
              show: false,
            },
            value: {
              show: true,
              fontSize: "var(--fs-large)",
              fontWeight: "bold",
              formatter: function (val) {
                return val + " sp";
              },
            },
            total: {
              show: false,
            },
          },
        },
        dataLabels: {
          offset: 0,
          minAngleToShowLabel: 10,
        },
      },
    },
    dataLabels: {
      enabled: true,
      formatter: function (val) {
        return val.toFixed(1) + "%";
      },
      style: {
        fontSize: "var(--fs-small)",
        fontFamily: "Arial, sans-serif",
        fontWeight: "bold",
        colors: ["#fff"],
      },
      dropShadow: {
        enabled: false,
      },
    },
    responsive: [
      {
        breakpoint: 480,
        options: {
          legend: {
            position: "bottom",
          },
        },
      },
    ],
  },

  // Language Chart Data
  languageSeries:
    languageCount && languageCount.length
      ? languageCount.map((item) => item.count)
      : [],
  languageOptions: {
    chart: {
      type: "donut",
    },
    labels:
      languageCount && languageCount.length
        ? languageCount.map((item) => item.name)
        : [],
    colors: ["#004D79", "#00AC69", "#E4006D"],
    legend: {
      position: "right",
    },
    plotOptions: {
      pie: {
        donut: {
          size: "45%",
          labels: {
            show: true,
            name: {
              show: false,
            },
            value: {
              show: true,
              fontSize: "var(--fs-large)",
              fontWeight: "bold",
              formatter: function (val) {
                return val + " sp";
              },
            },
            total: {
              show: false,
            },
          },
        },
        dataLabels: {
          offset: 0,
          minAngleToShowLabel: 10,
        },
      },
    },
    dataLabels: {
      enabled: true,
      formatter: function (val) {
        return val.toFixed(1) + "%";
      },
      style: {
        fontSize: "var(--fs-small)",
        fontFamily: "Arial, sans-serif",
        fontWeight: "bold",
        colors: ["#fff"],
      },
      dropShadow: {
        enabled: false,
      },
    },
    responsive: [
      {
        breakpoint: 480,
        options: {
          legend: {
            position: "bottom",
          },
        },
      },
    ],
  },
});
</script>

<style scoped>
.chart-container {
  display: grid;
  grid-template-columns: repeat(2, 1fr);
  gap: 20px;
  margin: 20px 0;
}

.chart-card {
  border: 1px solid #e0e0e0;
  border-radius: 10px;
  padding: 15px;
  background-color: white;
  box-shadow: 0 2px 4px rgba(0, 0, 0, 0.05);
}

.chart-card h3 {
  margin-top: 0;
  color: #333;
  font-size: var(--fs-medium);
  font-weight: 500;
  margin-bottom: 15px;
}

.chart-content {
  min-height: 300px;
}

.chart-empty-state {
  align-items: center;
  color: var(--neutral-700);
  display: flex;
  font-size: var(--fs-medium);
  height: 300px;
  justify-content: center;
  text-align: center;
}

@media (max-width: 768px) {
  .chart-container {
    grid-template-columns: 1fr;
  }
}
</style>
